/**
 * TB Mukt Bharat - Live Streams Page
 * Fast & Lightweight - Uses GovLayout Components
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'
import liveStreamService, { LiveStream } from '../../services/liveStreamService'
import { GovLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/layout/GovLayout'
import { Video, Users, Calendar, Play, Radio, Camera } from 'lucide-react'

// ================================================================================
// STREAM CARD COMPONENT - Memoized for performance
// ================================================================================
const StreamCard = React.memo(({ stream, onJoin }: { stream: LiveStream; onJoin: (id: number) => void }) => {
  const statusBadge = useMemo(() => {
    switch (stream.status) {
      case 'LIVE':
        return <span className="px-2 py-1 text-xs font-medium bg-red-500 text-white rounded-full">LIVE</span>
      case 'SCHEDULED':
        return <span className="px-2 py-1 text-xs font-medium bg-blue-500 text-white rounded-full">Scheduled</span>
      case 'ENDED':
        return <span className="px-2 py-1 text-xs font-medium bg-gray-500 text-white rounded-full">Ended</span>
      default:
        return null
    }
  }, [stream.status])

  const formattedDate = useMemo(() => {
    const date = new Date(stream.startedAt || stream.createdAt)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
  }, [stream.startedAt, stream.createdAt])

  return (
    <GovCard className="overflow-hidden cursor-pointer hover:shadow-md transition-shadow" onClick={() => onJoin(stream.id)}>
      <div className="relative aspect-video bg-gray-900 -mx-4 -mt-4 mb-4">
        <div className="absolute top-2 left-2 z-10">{statusBadge}</div>
        <div className="absolute top-2 right-2 z-10 flex items-center gap-1 bg-black/60 text-white px-2 py-1 rounded text-sm">
          <Users className="w-4 h-4" />
          {stream.viewerCount}
        </div>
        {stream.thumbnailUrl ? (
          <img src={stream.thumbnailUrl} alt={stream.title} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-red-900/50 to-orange-900/50">
            <Video className="w-12 h-12 text-white/50" />
          </div>
        )}
        <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 hover:opacity-100 transition-opacity">
          <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
            <Play className="w-8 h-8 text-white fill-white" />
          </div>
        </div>
      </div>
      <h3 className="font-medium mb-2 line-clamp-2">{stream.title}</h3>
      <Link to={`/profile/${stream.host.username}`} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700" onClick={(e) => e.stopPropagation()}>
        <img src={stream.host.profileImage || '/default-avatar.svg'} alt="" className="w-6 h-6 rounded-full object-cover" loading="lazy" />
        <span>{stream.host.firstName} {stream.host.lastName}</span>
      </Link>
      <p className="text-xs text-gray-500 mt-2">Started {formattedDate}</p>
    </GovCard>
  )
})

// ================================================================================
// SCHEDULED STREAM CARD - Memoized for performance
// ================================================================================
const ScheduledStreamCard = React.memo(({ stream }: { stream: LiveStream }) => {
  const dateInfo = useMemo(() => {
    const date = new Date(stream.startedAt || stream.createdAt)
    return {
      day: date.getDate(),
      month: date.toLocaleDateString('en-US', { month: 'short' }),
      time: date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    }
  }, [stream.startedAt, stream.createdAt])

  const statusBadge = useMemo(() => {
    switch (stream.status) {
      case 'LIVE':
        return <span className="px-2 py-1 text-xs font-medium bg-red-500 text-white rounded-full">LIVE</span>
      case 'SCHEDULED':
        return <span className="px-2 py-1 text-xs font-medium bg-blue-500 text-white rounded-full">Scheduled</span>
      default:
        return null
    }
  }, [stream.status])

  return (
    <GovCard className="flex gap-4">
      <div className="flex-shrink-0 w-24 text-center">
        <div className="text-2xl font-bold">{dateInfo.day}</div>
        <div className="text-sm text-gray-500">{dateInfo.month}</div>
      </div>
      <div className="flex-1">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="font-medium">{stream.title}</h3>
            <p className="text-sm text-gray-500 line-clamp-2">{stream.description}</p>
          </div>
          {statusBadge}
        </div>
        <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
          <img src={stream.host.profileImage || '/default-avatar.svg'} alt="" className="w-5 h-5 rounded-full object-cover" loading="lazy" />
          <span>{stream.host.firstName} {stream.host.lastName}</span>
          <span>•</span>
          <span>{dateInfo.time}</span>
        </div>
      </div>
    </GovCard>
  )
})

// ================================================================================
// MAIN PAGE COMPONENT
// ================================================================================
const LiveStreamsPage = () => {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()

  const [activeStreams, setActiveStreams] = useState<LiveStream[]>([])
  const [scheduledStreams, setScheduledStreams] = useState<LiveStream[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'live' | 'scheduled'>('live')

  useEffect(() => {
    const fetchStreams = async () => {
      try {
        setLoading(true)
        const [activeRes, scheduledRes] = await Promise.all([
          liveStreamService.getActiveStreams(0, 20),
          liveStreamService.getScheduledStreams()
        ])
        if (activeRes.success) setActiveStreams(activeRes.data.streams || activeRes.data || [])
        if (scheduledRes.success) setScheduledStreams(scheduledRes.data || [])
      } catch (error) {
        console.error('Error fetching streams:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchStreams()
  }, [])

  const handleJoinStream = useCallback((streamId: number) => {
    navigate(`/live/${streamId}`)
  }, [navigate])

  const handleGoLive = useCallback(() => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }
    navigate('/live/start')
  }, [isAuthenticated, navigate])

  const tabs = useMemo(() => [
    { key: 'live' as const, label: 'Live Now', count: activeStreams.length, icon: Video },
    { key: 'scheduled' as const, label: 'Upcoming', count: scheduledStreams.length, icon: Calendar }
  ], [activeStreams.length, scheduledStreams.length])

  if (loading) {
    return (
      <GovLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </GovLayout>
    )
  }

  return (
    <GovLayout>
      <GovPageTitle title="Live Streams" subtitle="Watch TB awareness sessions and discussions in real-time" icon={Radio} />

      {/* Quick Go Live Card */}
      <GovCard className="mb-6 bg-gradient-to-r from-red-50 to-orange-50 border-red-200">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <Video className="w-6 h-6 text-red-500" />
            <div>
              <h2 className="font-semibold">Start Your Live Stream</h2>
              <p className="text-sm text-gray-500">Share TB awareness content with your audience</p>
            </div>
          </div>
          <GovButton variant="primary" onClick={handleGoLive} className="bg-red-600 hover:bg-red-700 flex items-center gap-2">
            <Camera className="w-4 h-4" />
            Go Live
          </GovButton>
        </div>
      </GovCard>

      {/* Tabs */}
      <div className="flex gap-4 mb-4 border-b border-gray-200">
        {tabs.map((tab) => {
          const Icon = tab.icon
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 font-medium transition-colors ${
                activeTab === tab.key
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label} ({tab.count})
            </button>
          )
        })}
      </div>

      {/* Content */}
      {activeTab === 'live' && (
        activeStreams.length === 0 ? (
          <GovEmptyState
            icon="📡"
            title="No Live Streams"
            description="Check back later for TB awareness sessions and discussions"
            action={{ label: 'Start a Live Stream', onClick: handleGoLive }}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {activeStreams.map((stream) => (
              <StreamCard key={stream.id} stream={stream} onJoin={handleJoinStream} />
            ))}
          </div>
        )
      )}

      {activeTab === 'scheduled' && (
        scheduledStreams.length === 0 ? (
          <GovEmptyState
            icon="📅"
            title="No Upcoming Streams"
            description="No streams scheduled. Check back later!"
          />
        ) : (
          <div className="space-y-3">
            {scheduledStreams.map((stream) => (
              <ScheduledStreamCard key={stream.id} stream={stream} />
            ))}
          </div>
        )
      )}
    </GovLayout>
  )
}

export default React.memo(LiveStreamsPage)